<template>
  <div class="page-container">
    <div class="page-header">
      <div class="header-left">
        <button class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleEdit">
          <i class="ri-edit-line"></i>
          編輯
        </button>
        <button class="btn btn-warning" @click="handleResetPassword" v-if="account">
          <i class="ri-lock-password-line"></i>
          重設密碼
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="loadAccount">重試</button>
    </div>

    <div v-else-if="account" class="detail-content">
      <!-- 基本資訊 -->
      <div class="info-section">
        <h3 class="section-title">基本資訊</h3>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">登入帳號：</span>
            <span class="info-value">{{ account.username }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Email：</span>
            <span class="info-value">{{ account.email || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">姓名：</span>
            <span class="info-value">{{ account.name || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">角色：</span>
            <span class="badge badge-info">{{ getAccountRoleLabel(account.role, account.roleName) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">狀態：</span>
            <span
              :class="account.status === 'active' ? 'badge badge-success' : 'badge badge-danger'"
            >
              {{ account.status === 'active' ? '啟用' : '停用' }}
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">建立時間：</span>
            <span class="info-value">{{ formatDate(account.createdAt) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">最後更新：</span>
            <span class="info-value">{{ formatDate(account.updatedAt) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">最後登入：</span>
            <span class="info-value">{{ formatDate(account.lastLoginAt) || '從未登入' }}</span>
          </div>
        </div>
      </div>

      <!-- 權限設定 -->
      <div class="info-section">
        <h3 class="section-title">權限設定</h3>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">擁有全部權限：</span>
            <span class="info-value">
              {{ account.hasAllPermissions ? '是' : '否' }}
            </span>
          </div>
          <div v-if="!account.hasAllPermissions && account.permissions && account.permissions.length > 0" class="info-item full-width">
            <span class="info-label">具體權限：</span>
            <div class="permissions-list">
              <span
                v-for="permission in account.permissions"
                :key="permission"
                class="badge badge-secondary"
              >
                {{ permission }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- 程式列表 -->
      <div class="info-section">
        <h3 class="section-title">程式列表</h3>
        <div v-if="loadingPrograms" class="loading-state-small">
          <div class="spinner"></div>
          <p>載入中...</p>
        </div>
        <div v-else-if="programsError" class="error-state-small">
          <p class="error-message">{{ programsError }}</p>
        </div>
        <div v-else-if="!programs || programs.length === 0" class="empty-state">
          沒有程式
        </div>
        <table v-else class="detail-table">
          <thead>
            <tr>
              <th>程式名稱</th>
              <th>程式代碼</th>
              <th>允許權限</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="program in programs" :key="program.id">
              <td>{{ program.programName }}</td>
              <td>{{ program.programCode }}</td>
              <td>
                <span v-if="!program.allowedPermissions || program.allowedPermissions.length === 0" class="text-muted">
                  全部權限
                </span>
                <span v-else>
                  <span
                    v-for="perm in program.allowedPermissions"
                    :key="perm"
                    class="badge badge-secondary"
                  >
                    {{ perm }}
                  </span>
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 狀態變更歷史 -->
      <div class="info-section">
        <h3 class="section-title">狀態變更歷史</h3>
        <div v-if="loadingStatusHistory" class="loading-state-small">
          <div class="spinner"></div>
          <p>載入中...</p>
        </div>
        <div v-else-if="statusHistoryError" class="error-state-small">
          <p class="error-message">{{ statusHistoryError }}</p>
        </div>
        <div v-else-if="!statusHistory || statusHistory.length === 0" class="empty-state">
          沒有狀態變更記錄
        </div>
        <table v-else class="detail-table">
          <thead>
            <tr>
              <th>變更時間</th>
              <th>舊狀態</th>
              <th>新狀態</th>
              <th>操作者</th>
              <th>原因</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="history in statusHistory" :key="history.id">
              <td>{{ formatDateTime(history.createdAt) }}</td>
              <td>
                <span v-if="history.oldStatus" :class="getStatusClass(history.oldStatus)">
                  {{ getStatusText(history.oldStatus) }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td>
                <span :class="getStatusClass(history.newStatus)">
                  {{ getStatusText(history.newStatus) }}
                </span>
              </td>
              <td>{{ history.changedByUsername || '-' }}</td>
              <td>{{ history.reason || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 活動記錄 -->
      <div class="info-section">
        <h3 class="section-title">活動記錄</h3>
        <div v-if="loadingActivityLogs" class="loading-state-small">
          <div class="spinner"></div>
          <p>載入中...</p>
        </div>
        <div v-else-if="activityLogsError" class="error-state-small">
          <p class="error-message">{{ activityLogsError }}</p>
        </div>
        <div v-else-if="!activityLogs || activityLogs.length === 0" class="empty-state">
          沒有活動記錄
        </div>
        <div v-else class="activity-logs">
          <div
            v-for="log in activityLogs"
            :key="log.id"
            class="activity-log-item"
          >
            <div class="log-time">{{ formatDateTime(log.createdAt) }}</div>
            <div class="log-action">
              <span class="badge badge-info">{{ log.action }}</span>
              <span v-if="log.resourceType" class="log-resource">
                {{ log.resourceType }}
              </span>
            </div>
            <div v-if="log.ipAddress" class="log-meta">
              IP: {{ log.ipAddress }}
            </div>
          </div>
        </div>
        <ListPagination
          v-if="activityLogsTotal > activityLogsPageSize"
          :page="activityLogsPage"
          :total-pages="Math.ceil(activityLogsTotal / activityLogsPageSize)"
          @change="loadActivityLogs"
        />
      </div>
    </div>

    <!-- 重設密碼模態框 -->
    <div v-if="showResetPasswordModal" class="modal-overlay" @click.self="closeResetPasswordModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">重設密碼</h3>
          <button class="btn-close" @click="closeResetPasswordModal">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="newPassword" class="required">新密碼</label>
            <input
              id="newPassword"
              v-model="resetPasswordForm.newPassword"
              type="password"
              class="form-control"
              :class="{ 'is-invalid': resetPasswordForm.errors.newPassword }"
              placeholder="請輸入新密碼（至少 8 個字元）"
            />
            <div v-if="resetPasswordForm.errors.newPassword" class="invalid-feedback">
              {{ resetPasswordForm.errors.newPassword }}
            </div>
          </div>
          <div class="form-group">
            <label for="confirmPassword" class="required">確認密碼</label>
            <input
              id="confirmPassword"
              v-model="resetPasswordForm.confirmPassword"
              type="password"
              class="form-control"
              :class="{ 'is-invalid': resetPasswordForm.errors.confirmPassword }"
              placeholder="請再次輸入新密碼"
            />
            <div v-if="resetPasswordForm.errors.confirmPassword" class="invalid-feedback">
              {{ resetPasswordForm.errors.confirmPassword }}
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeResetPasswordModal">取消</button>
          <button class="btn btn-primary" @click="submitResetPassword" :disabled="resettingPassword">
            <span v-if="resettingPassword">處理中...</span>
            <span v-else>確認重設</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { systemAccountService } from '@/api/modules/systemAccounts.js';
import { accountProgramService } from '@/api/modules/accountPrograms.js';
import { showSuccess, showError } from '@/utils/swal.js';
import { getAccountRoleLabel } from '@/utils/accountRoleLabel.js';
import apiClient from '@/api/axios.js';

export default {
  name: 'SystemAccountDetail',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { pageTitle } = usePageTitle('系統帳號詳情');
    const account = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const programs = ref([]);
    const loadingPrograms = ref(false);
    const programsError = ref(null);
    const activityLogs = ref([]);
    const loadingActivityLogs = ref(false);
    const activityLogsError = ref(null);
    const activityLogsPage = ref(1);
    const activityLogsPageSize = ref(20);
    const activityLogsTotal = ref(0);
    const statusHistory = ref([]);
    const loadingStatusHistory = ref(false);
    const statusHistoryError = ref(null);
    const showResetPasswordModal = ref(false);
    const resettingPassword = ref(false);
    const resetPasswordForm = ref({
      newPassword: '',
      confirmPassword: '',
      errors: {}
    });

    const loadAccount = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await systemAccountService.getAccount(route.params.id);

        if (response && response.success && response.data) {
          account.value = response.data;
        } else {
          error.value = response?.message || '獲取帳號詳情失敗';
        }
      } catch (err) {
        console.error('獲取帳號詳情失敗:', err);
        error.value = err.message || '獲取帳號詳情失敗';
      } finally {
        loading.value = false;
      }
    };

    const loadPrograms = async () => {
      if (!account.value) return;

      loadingPrograms.value = true;
      programsError.value = null;

      try {
        const response = await accountProgramService.getProgramsByAccountId(
          account.value.id,
          'system'
        );

        if (response && response.success && response.data) {
          programs.value = response.data.programs || [];
        } else {
          programsError.value = response?.message || '獲取程式列表失敗';
        }
      } catch (err) {
        console.error('獲取程式列表失敗:', err);
        programsError.value = err.message || '獲取程式列表失敗';
      } finally {
        loadingPrograms.value = false;
      }
    };

    const loadStatusHistory = async () => {
      if (!account.value) return;

      loadingStatusHistory.value = true;
      statusHistoryError.value = null;

      try {
        const response = await systemAccountService.getStatusHistory(account.value.id);

        if (response && response.success && response.data) {
          statusHistory.value = response.data.items || [];
        } else {
          statusHistoryError.value = response?.message || '獲取狀態變更歷史失敗';
        }
      } catch (err) {
        console.error('獲取狀態變更歷史失敗:', err);
        statusHistoryError.value = err.message || '獲取狀態變更歷史失敗';
      } finally {
        loadingStatusHistory.value = false;
      }
    };

    const loadActivityLogs = async (page = 1) => {
      if (!account.value) return;

      loadingActivityLogs.value = true;
      activityLogsError.value = null;
      activityLogsPage.value = page;

      try {
        const response = await apiClient.get('/audit-logs/system', {
          params: {
            accountId: account.value.id,
            page: page,
            pageSize: activityLogsPageSize.value
          }
        });

        if (response && response.success && response.data) {
          activityLogs.value = response.data.items || [];
          activityLogsTotal.value = response.data.total || 0;
        } else {
          activityLogsError.value = response?.message || '獲取活動記錄失敗';
        }
      } catch (err) {
        console.error('獲取活動記錄失敗:', err);
        activityLogsError.value = err.message || '獲取活動記錄失敗';
      } finally {
        loadingActivityLogs.value = false;
      }
    };

    const handleBack = () => {
      router.push('/system/accounts');
    };

    const handleEdit = () => {
      router.push(`/system/accounts/${route.params.id}/edit`);
    };

    const handleResetPassword = () => {
      resetPasswordForm.value = {
        newPassword: '',
        confirmPassword: '',
        errors: {}
      };
      showResetPasswordModal.value = true;
    };

    const closeResetPasswordModal = () => {
      showResetPasswordModal.value = false;
      resetPasswordForm.value = {
        newPassword: '',
        confirmPassword: '',
        errors: {}
      };
    };

    const validateResetPasswordForm = () => {
      const errors = {};

      if (!resetPasswordForm.value.newPassword) {
        errors.newPassword = '請輸入新密碼';
      } else if (resetPasswordForm.value.newPassword.length < 8) {
        errors.newPassword = '密碼至少需要 8 個字元';
      }

      if (!resetPasswordForm.value.confirmPassword) {
        errors.confirmPassword = '請確認密碼';
      } else if (resetPasswordForm.value.newPassword !== resetPasswordForm.value.confirmPassword) {
        errors.confirmPassword = '兩次輸入的密碼不一致';
      }

      resetPasswordForm.value.errors = errors;
      return Object.keys(errors).length === 0;
    };

    const submitResetPassword = async () => {
      if (!validateResetPasswordForm()) {
        return;
      }

      resettingPassword.value = true;

      try {
        const response = await systemAccountService.resetPassword(route.params.id, {
          newPassword: resetPasswordForm.value.newPassword
        });

        if (response && response.success) {
          await showSuccess('重設成功', '密碼已成功重設');
          closeResetPasswordModal();
        } else {
          await showError('重設失敗', response?.message || '重設密碼失敗');
        }
      } catch (err) {
        console.error('重設密碼失敗:', err);
        await showError('重設失敗', err.message || '重設密碼失敗');
      } finally {
        resettingPassword.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW');
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    };

    const getStatusClass = (status) => {
      const statusMap = {
        'active': 'badge badge-success',
        'inactive': 'badge badge-danger',
        'suspended': 'badge badge-warning'
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getStatusText = (status) => {
      const statusMap = {
        'active': '啟用',
        'inactive': '停用',
        'suspended': '暫停'
      };
      return statusMap[status] || status;
    };

    onMounted(async () => {
      await loadAccount();
      if (account.value) {
        await loadPrograms();
        await loadStatusHistory();
        await loadActivityLogs();
      }
    });

    return {
      account,
      loading,
      error,
      programs,
      loadingPrograms,
      programsError,
      activityLogs,
      loadingActivityLogs,
      activityLogsError,
      activityLogsPage,
      activityLogsPageSize,
      activityLogsTotal,
      statusHistory,
      loadingStatusHistory,
      statusHistoryError,
      showResetPasswordModal,
      resettingPassword,
      resetPasswordForm,
      pageTitle,
      loadAccount,
      loadStatusHistory,
      loadActivityLogs,
      handleBack,
      handleEdit,
      handleResetPassword,
      closeResetPasswordModal,
      submitResetPassword,
      formatDate,
      formatDateTime,
      getStatusClass,
      getStatusText,
      getAccountRoleLabel
    };
  }
};
</script>

<style scoped>
.page-container {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.info-section {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.info-item {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-weight: 500;
  color: #666;
  white-space: nowrap;
}

.info-value {
  color: #333;
}

.permissions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
}

.detail-table th,
.detail-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.detail-table th {
  background: #f5f5f5;
  font-weight: 600;
}

.activity-logs {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.activity-log-item {
  padding: 12px;
  background: #f9f9f9;
  border-radius: 4px;
  border-left: 3px solid #3b82f6;
}

.log-time {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.log-action {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.log-resource {
  color: #666;
  font-size: 14px;
}

.log-meta {
  font-size: 12px;
  color: #999;
}

.loading-state-small,
.error-state-small {
  padding: 20px;
  text-align: center;
}

.empty-state {
  padding: 20px;
  text-align: center;
  color: #999;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
}

.page-info {
  color: #666;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #f8f9fa;
  color: #333;
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

@import '~@/assets/scss/components/_table-common.scss';
</style>
